import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { PdfPreview } from '../FileLibrary';
import { DayFirstDateInput, dmyToIso, isoToDmy } from '../DocStorage/UploadWizard';
import PopupHomeNotice from '../PopupHomeNotice';
import { usePopupHome, HOME_FILING } from '../../services/popupHome';   // Filing, one tab only (report c2846304)

// ── Drop File: the AI's proposals (Calem, report df26fdac) ──────────────────────────────────────────
// "Ask AI to analyze the files dropped in the drop folder (by a specific user) and once AI has determined
// the naming protocol and folder destination, AI can pop-up on the screen of that user's PC or laptop,
// showing the document or documents they dropped in the drop folder, with all the relevant info ready to
// store like; doc name, person, company, date, folder location, etc. then, ask the user to confirm
// everything is correct then store those documents in their appropriate locations."
// A document Calem drops into Drop File under any other name is read on the server
// (server/src/services/dropFile.ts), and so is every JPEG, PNG or PDF anyone else drops there, however it
// is named (3rd opening: "only bring the pop-up to my screen (Calem)"). This watcher pops each proposal up,
// one at a time, saying who dropped it: the document itself beside its document name, person, company,
// date and folder — every one editable — and the name it will be stored under. Confirm renames it and
// files it there (a same-name file already in that folder is asked about first, as Drop File always asks);
// Skip leaves it in Drop File as it is.
// Report cc1af090 ("When anyone uploads a document to the drop folder, give Calem a pop-up notification on his
// screen"): a file the AI can't read — a Word file, a spreadsheet, anything over 10 MB — comes up in the same
// queue as a notice: a simple card saying who dropped it, with its name, type and size, and the file itself when
// it is a PDF. Open Drop File takes him to it; OK closes it. Either one answers it, and nothing is filed from it.
// Calem only while it is tried out: mounted once in AuthGate, and nobody else ever asks the server.
// Switched off since 2026-09-30 (reports 296f15b9, 2c9fa16f): the Drop Folder is a plain inbox that files and reads
// nothing, so the server hands out no proposals or notices (routes/files.ts) and this never opens.

// The 'ai_reviewer' role, read from /api/me (me.roles); the server enforces the same role.
const POLL_MS = 60_000;
const READING_MS = 5_000;                   // while the server is still reading one of the dropped files
// Never opens under someone mid-sentence — keyed on keystrokes, as JobCommentPing / JobStatusConfirm are.
// Once it is open, typing into its own boxes keeps it open.
const TYPING_IDLE_MS = 4000;

type Row = {
  id: string; name: string; mime: string; size: number; ext: string; status: 'ready' | 'failed' | 'notice'; error: string;
  type: string; person: string; company: string; date: string; dateKind: string; folderId: string; folderPath: string;
  byName: string;   // who dropped it
  dropFolderId?: string;   // a notice: the Drop File it waits in (Open Drop File)
};
type Draft = { type: string; person: string; company: string; dateTxt: string; folderId: string; folderTouched: boolean };

// The server's cleanPart and the upload wizard's clean: what a name part may not carry.
const clean = (s: string) => String(s || '').replace(/[/\\:*?"<>|()]/g, ' ').replace(/\s+/g, ' ').trim();
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', borderRadius: 8, color: 'var(--text-primary)', boxSizing: 'border-box' };
const lbl: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-secondary)', display: 'block', margin: '10px 0 4px' };
// A notice's size, as the File Library's Size column writes it.
const fmtSize = (n: number) => (!n ? '—' : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);

export default function DropProposals() {
  const { t } = useTranslation();
  const loc = useLocation();
  const me = useStore(s => s.me);
  const showToast = useStore(s => s.showToast);
  const mine = !!me && (me.roles || []).includes('ai_reviewer');

  const [rows, setRows] = useState<Row[]>([]);
  const [types, setTypes] = useState<{ name: string; folderId: string }[]>([]);
  const [folders, setFolders] = useState<{ id: string; path: string }[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [folderOpen, setFolderOpen] = useState(false);
  const [folderQ, setFolderQ] = useState('');
  const [preview, setPreview] = useState<{ id: string; url: string } | null>(null);
  const [previewErr, setPreviewErr] = useState(false);

  const [typing, setTyping] = useState(false);
  const lastKey = useRef(0);
  const shown = useRef(false);
  useEffect(() => {
    if (!mine) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) { lastKey.current = Date.now(); setTyping(true); }
    };
    document.addEventListener('keydown', onKey, true);
    const id = setInterval(() => setTyping(Date.now() - lastKey.current < TYPING_IDLE_MS), 1000);
    return () => { document.removeEventListener('keydown', onKey, true); clearInterval(id); };
  }, [mine]);

  // Each look sets when the next one is due: every few seconds while one of his files is still being
  // read, else once a minute.
  const [wait, setWait] = useState(POLL_MS);
  const [beat, setBeat] = useState(0);
  const poll = useCallback(async () => {
    if (!localStorage.getItem('mf_token')) return;
    try {
      const d: any = await api.filesDropProposals();
      setRows(d.rows || []);
      if (d.types) setTypes(d.types);
      if (d.folders) setFolders(d.folders);
      setWait(Number(d.reading) > 0 ? READING_MS : POLL_MS);
    } catch { setWait(POLL_MS); /* transient — try again next time */ }
    setBeat(b => b + 1);
  }, []);
  useEffect(() => {
    if (!mine) return;
    const id = setTimeout(poll, wait);
    return () => clearTimeout(id);
  }, [mine, wait, beat, poll]);
  // … and at once when he moves around, comes back to this tab, or has just dropped a file (FileLibrary).
  useEffect(() => { if (mine) void poll(); }, [mine, loc.pathname, poll]);
  useEffect(() => {
    if (!mine) return;
    const now = () => { void poll(); };
    const onVisible = () => { if (document.visibilityState === 'visible') void poll(); };
    window.addEventListener('slf:drop-proposals', now);
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.removeEventListener('slf:drop-proposals', now); document.removeEventListener('visibilitychange', onVisible); };
  }, [mine, poll]);
  useEffect(() => { if (!mine) setRows([]); }, [mine]);

  const current = rows[0];
  const curId = current?.id || '';
  // A notice shows its file only when PdfPreview can: a Word file or a spreadsheet is never fetched.
  const showFile = !!current && (current.status !== 'notice' || current.mime === 'application/pdf');
  // The document itself, fetched as the File Library's preview does; its blob URL lives as long as it is shown.
  useEffect(() => {
    setFolderOpen(false); setFolderQ(''); setErr('');
    setPreview(null); setPreviewErr(false);
    if (!curId || !showFile) return;
    let live = true; let url = '';
    api.filesBlob(curId)
      .then(u => { url = u; if (live) setPreview({ id: curId, url: u }); else URL.revokeObjectURL(u); })
      .catch(() => { if (live) setPreviewErr(true); });
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [curId, showFile]);

  // Report c2846304: a dropped document is filed from Filing, so the proposal is put up in the tab showing that
  // page and in no other; with the page open in no tab, the tab in front offers to open it (PopupHomeNotice).
  const pending = !!mine && !!current && !(typing && !shown.current);
  const popHome = usePopupHome(HOME_FILING, pending);
  if (!mine || !current || (typing && !shown.current)) { shown.current = false; return null; }
  shown.current = true;
  if (popHome !== 'show') return popHome === 'notice' ? <PopupHomeNotice page={HOME_FILING} /> : null;

  const base: Draft = {
    type: current.type, person: current.person, company: current.company,
    dateTxt: current.date ? isoToDmy(current.date) : '', folderId: current.folderId, folderTouched: false,
  };
  const d = drafts[current.id] || base;
  const setDraft = (patch: Partial<Draft>) => setDrafts(ds => ({ ...ds, [current.id]: { ...(ds[current.id] || base), ...patch } }));
  // A new document name takes that type's own folder with it, unless the folder was picked by hand.
  const pickType = (name: string) => {
    const home = types.find(x => x.name === name)?.folderId || '';
    setDraft(!d.folderTouched && home ? { type: name, folderId: home } : { type: name });
  };
  const done = (id: string) => {
    setRows(rs => rs.filter(r => r.id !== id));
    setDrafts(ds => { const next = { ...ds }; delete next[id]; return next; });
    setErr('');
  };

  const iso = dmyToIso(d.dateTxt);
  const company = clean(d.company);
  const storedAs = d.type && clean(d.person) && iso ? `${clean(d.type)} (${clean(d.person)})${company ? ` ${company}` : ''} ${iso}${current.ext}` : '';
  const folderPath = folders.find(f => f.id === d.folderId)?.path || (d.folderId && d.folderId === current.folderId ? current.folderPath : '');
  const ready = !!storedAs && !!folderPath;
  const q = folderQ.trim().toLowerCase();
  const shownFolders = (q ? folders.filter(f => f.path.toLowerCase().includes(q)) : folders).slice(0, 300);
  // Where the date came from — said only while it is still the AI's (a date typed by hand needs no note).
  const dateHint = d.dateTxt && !iso ? t('ds_wiz_date_invalid')
    : iso && iso !== current.date ? ''
    : current.dateKind === 'expiry' ? t('dfp_date_expiry') : current.dateKind === 'issue' ? t('dfp_date_issue') : t('dfp_date_none');

  // Confirm: rename + file. A same-name file in that folder is asked about (Replace → the old one to the
  // Trash); No keeps the document here with the reason, to change the name or folder or to Skip.
  const confirm = async () => {
    if (busy || !ready) return;
    setBusy(true); setErr('');
    const fields = { type: d.type, person: clean(d.person), company, date: iso, folderId: d.folderId };
    try {
      let replace = false;
      for (;;) {
        const r: any = await api.filesDropProposalConfirm(current.id, fields, replace);
        const drop = r?.drop || {};
        if (drop.moved) { showToast(t('dfp_filed', { name: drop.name, path: drop.moved.folderPath })); done(current.id); window.dispatchEvent(new Event('slf:files-changed')); break; }
        if (drop.conflict && !replace && window.confirm(t('fl_drop_replace_confirm', { name: drop.name, path: drop.conflict.folderPath }))) { replace = true; continue; }
        if (drop.note) setErr(t(`fl_drop_note_${drop.note.code}`, { type: drop.note.type || '', path: drop.note.path || '' }));
        window.dispatchEvent(new Event('slf:files-changed'));   // it may have taken its new name in Drop File
        break;
      }
    } catch (e: any) {
      // Gone already — answered in another tab, or the file was moved or renamed by hand.
      if (e?.status === 404) { showToast(e.message); done(current.id); }
      else setErr(e?.message || 'Failed');
    } finally { setBusy(false); }
  };
  const skip = async () => {
    if (busy) return;
    setBusy(true);
    try { await api.filesDropProposalDismiss(current.id); } catch { /* still there → the next look brings it back */ }
    done(current.id);
    setBusy(false);
  };

  const btn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, fontWeight: 600, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' };
  // A notice: nothing to fill in, nothing filed from it — who dropped which file, and the file itself when it is
  // a PDF. Open Drop File opens Document Storage in that Drop File, the file marked, in a new tab (the link the
  // upload wizard opens a folder with), so the page underneath stays as it was; like OK, it answers the notice.
  if (current.status === 'notice') {
    const kind = current.ext ? current.ext.slice(1).toUpperCase() : current.mime && current.mime !== 'application/octet-stream' ? current.mime : '—';
    const openDrop = () => {
      if (busy) return;
      try { window.open(`/m/doc_storage?folder=${encodeURIComponent(current.dropFolderId || '')}&focus=${encodeURIComponent(current.name)}`, '_blank'); } catch { /* blocked */ }
      void skip();
    };
    const val: React.CSSProperties = { fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', overflowWrap: 'anywhere' };
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div role="dialog" aria-modal="true" style={{ background: 'var(--bg-surface)', borderRadius: 14, width: showFile ? 920 : 520, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-md)' }}>
          <div style={{ padding: '16px 22px 12px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <i className="ti ti-file-upload" style={{ fontSize: 22, color: 'var(--accent)' }} aria-hidden />
              <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t('dfp_notice_title')}</div>
              {rows.length > 1 && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', background: 'var(--c-gray-100)', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>1 / {rows.length}</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>{t('dfp_notice_hint')}</div>
          </div>

          <div style={{ display: 'flex', gap: 18, padding: '14px 22px', minHeight: 0, flex: 1, overflow: 'auto', flexWrap: 'wrap' }}>
            {showFile && (
              <div style={{ flex: '1 1 380px', minWidth: 0, maxHeight: '64vh', overflowY: 'auto', background: 'var(--bg-app)', border: '1px solid var(--border)', borderRadius: 10, padding: 10, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                {previewErr ? (
                  <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13, alignSelf: 'center' }}>
                    <i className="ti ti-file-alert" style={{ fontSize: 28, display: 'block', marginBottom: 8 }} aria-hidden />{t('fl_preview_fail')}
                  </div>
                ) : !preview || preview.id !== current.id ? (
                  <i className="ti ti-loader" style={{ fontSize: 22, color: 'var(--text-tertiary)', display: 'inline-block', animation: 'spin 1s linear infinite', alignSelf: 'center' }} aria-hidden />
                ) : (
                  <PdfPreview src={preview.url} />
                )}
              </div>
            )}

            <div style={{ flex: '1 1 340px', minWidth: 0 }}>
              {current.byName && (
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3, overflowWrap: 'anywhere' }}>
                  <i className="ti ti-user" style={{ marginRight: 5, color: 'var(--accent)' }} aria-hidden />{t('dfp_dropped_by', { name: current.byName })}
                </div>
              )}
              <div style={lbl}>{t('dfp_notice_name')}</div>
              <div style={val}>{current.name}</div>
              <div style={lbl}>{t('dfp_notice_type')}</div>
              <div style={val}>{kind}</div>
              <div style={lbl}>{t('dfp_notice_size')}</div>
              <div style={val}>{fmtSize(current.size)}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, padding: '12px 22px 18px', borderTop: '1px solid var(--border)' }}>
            <button onClick={openDrop} disabled={busy} style={{ ...btn, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, opacity: busy ? 0.7 : 1 }}>
              <i className="ti ti-folder-open" style={{ fontSize: 14 }} aria-hidden />{t('dfp_notice_open')}
            </button>
            <button onClick={skip} disabled={busy} style={{ ...btn, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)' }}>
              <i className="ti ti-check" style={{ fontSize: 14 }} aria-hidden />{t('dfp_notice_ok')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    // One step under the job pop-ups (5500): a status email waiting on its confirmation is more urgent.
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div role="dialog" aria-modal="true" style={{ background: 'var(--bg-surface)', borderRadius: 14, width: 920, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: '16px 22px 12px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <i className="ti ti-file-search" style={{ fontSize: 22, color: 'var(--accent)' }} aria-hidden />
            <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t('dfp_title')}</div>
            {rows.length > 1 && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', background: 'var(--c-gray-100)', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>1 / {rows.length}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>{t('dfp_hint')}</div>
        </div>

        <div style={{ display: 'flex', gap: 18, padding: '14px 22px', minHeight: 0, flex: 1, overflow: 'auto', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 380px', minWidth: 0, maxHeight: '64vh', overflowY: 'auto', background: 'var(--bg-app)', border: '1px solid var(--border)', borderRadius: 10, padding: 10, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
            {previewErr ? (
              <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13, alignSelf: 'center' }}>
                <i className="ti ti-file-alert" style={{ fontSize: 28, display: 'block', marginBottom: 8 }} aria-hidden />{t('fl_preview_fail')}
              </div>
            ) : !preview || preview.id !== current.id ? (
              <i className="ti ti-loader" style={{ fontSize: 22, color: 'var(--text-tertiary)', display: 'inline-block', animation: 'spin 1s linear infinite', alignSelf: 'center' }} aria-hidden />
            ) : current.mime === 'application/pdf' ? (
              <PdfPreview src={preview.url} />
            ) : (
              <img src={preview.url} alt={current.name} style={{ maxWidth: '100%', display: 'block', borderRadius: 4, background: '#fff' }} />
            )}
          </div>

          <div style={{ flex: '1 1 340px', minWidth: 0 }}>
            {current.byName && (
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3, overflowWrap: 'anywhere' }}>
                <i className="ti ti-user" style={{ marginRight: 5, color: 'var(--accent)' }} aria-hidden />{t('dfp_dropped_by', { name: current.byName })}
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', overflowWrap: 'anywhere' }}>
              <i className="ti ti-file-upload" style={{ marginRight: 5 }} aria-hidden />{t('dfp_dropped_as', { name: current.name })}
            </div>
            {current.status === 'failed' && (
              <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: 12, color: '#92400E', lineHeight: 1.5 }}>
                <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} aria-hidden />{t('dfp_failed')}
                {current.error && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, overflowWrap: 'anywhere' }}>{current.error}</div>}
              </div>
            )}

            <label style={lbl}>{t('dfp_doc_name')}</label>
            <select value={d.type} onChange={e => pickType(e.target.value)} style={inp}>
              <option value="">{t('dfp_pick')}</option>
              {d.type && !types.some(x => x.name === d.type) ? <option value={d.type}>{d.type}</option> : null}
              {types.map(x => <option key={x.name} value={x.name}>{x.name}</option>)}
            </select>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 8 }}>
              <div>
                <label style={lbl}>{t('dfp_person')}</label>
                <input value={d.person} onChange={e => setDraft({ person: e.target.value })} placeholder={t('dfp_person_ph')} style={inp} />
              </div>
              <div>
                <label style={lbl}>{t('dfp_company')}</label>
                <input value={d.company} onChange={e => setDraft({ company: e.target.value })} placeholder={t('dfp_company_ph')} style={inp} />
              </div>
            </div>

            <label style={lbl}>{t('dfp_date')}</label>
            <div style={{ width: 170 }}>
              <DayFirstDateInput value={d.dateTxt} onChange={v => setDraft({ dateTxt: v })} />
            </div>
            {dateHint && <div style={{ marginTop: 3, fontSize: 11, color: d.dateTxt && !iso ? '#B45309' : 'var(--text-tertiary)' }}>{dateHint}</div>}

            <label style={lbl}>{t('dfp_folder')}</label>
            {!folderOpen ? (
              <div onClick={() => { setFolderOpen(true); setFolderQ(''); }}
                style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-surface)' }}>
                <i className="ti ti-folder" style={{ color: folderPath ? 'var(--accent)' : '#B45309', flexShrink: 0 }} aria-hidden />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, color: folderPath ? 'var(--text-primary)' : '#B45309' }}>{folderPath || t('dfp_folder_pick')}</span>
                <i className="ti ti-chevron-down" style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden />
              </div>
            ) : (
              <>
                <input autoFocus value={folderQ} onChange={e => setFolderQ(e.target.value)} placeholder={t('dfp_folder_filter')} autoComplete="off" style={{ ...inp, marginBottom: 4 }} />
                <div style={{ border: '1px solid var(--border-strong)', borderRadius: 8, maxHeight: 180, overflowY: 'auto', background: 'var(--c-gray-50)' }}>
                  {shownFolders.map(f => (
                    <div key={f.id} onClick={() => { setDraft({ folderId: f.id, folderTouched: true }); setFolderOpen(false); }}
                      style={{ padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid var(--border)',
                        background: f.id === d.folderId ? 'var(--bg-active)' : 'transparent', fontWeight: f.id === d.folderId ? 700 : 400, color: 'var(--text-secondary)' }}>
                      {f.id === d.folderId && <i className="ti ti-check" style={{ fontSize: 13, flexShrink: 0 }} aria-hidden />}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.path}</span>
                    </div>
                  ))}
                  {!shownFolders.length && <div style={{ padding: 10, fontSize: 12, color: 'var(--text-tertiary)' }}>{t('dfp_no_match')}</div>}
                </div>
              </>
            )}

            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 9, border: '1.5px solid var(--accent)', background: 'var(--accent-bg)' }}>
              <i className="ti ti-file-text" style={{ fontSize: 22, color: ready ? 'var(--accent)' : '#B45309', flexShrink: 0 }} aria-hidden />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--accent-text)' }}>{t('dfp_stored_as')}</div>
                <div style={{ marginTop: 1, fontSize: 13.5, fontWeight: 700, color: storedAs ? 'var(--text-primary)' : '#B45309', overflowWrap: 'anywhere' }}>{storedAs || t('dfp_missing')}</div>
                <div style={{ marginTop: 2, fontSize: 11.5, color: folderPath ? 'var(--text-secondary)' : '#B45309', overflowWrap: 'anywhere' }}>
                  <i className="ti ti-folder" style={{ marginRight: 4 }} aria-hidden />{folderPath || t('dfp_folder_pick')}
                </div>
              </div>
            </div>
            {err && <div style={{ marginTop: 8, fontSize: 12, color: '#E24B4A', overflowWrap: 'anywhere' }}>{err}</div>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '12px 22px 18px', borderTop: '1px solid var(--border)' }}>
          <button onClick={confirm} disabled={busy || !ready}
            style={{ ...btn, border: 'none', background: ready ? 'var(--accent)' : 'var(--c-gray-100)', color: ready ? '#fff' : 'var(--text-tertiary)', fontWeight: 700, opacity: busy ? 0.7 : 1, cursor: busy || !ready ? 'not-allowed' : 'pointer' }}>
            <i className={busy ? 'ti ti-loader-2' : 'ti ti-check'} style={{ fontSize: 14 }} aria-hidden />{t('dfp_confirm')}
          </button>
          <button onClick={skip} disabled={busy} title={t('dfp_skip_hint')} style={{ ...btn, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)' }}>
            <i className="ti ti-player-skip-forward" style={{ fontSize: 14 }} aria-hidden />{t('dfp_skip')}
          </button>
        </div>
      </div>
    </div>
  );
}
